import React, { useRef, useEffect, useState, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import WaveSurfer from "wavesurfer.js";
import { Play, Pause } from "lucide-react";

const LS_NAME = "audioMessageRate";
const RATES = [1, 1.5, 2, 0.5];

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		alignItems: "center",
		gap: ({ compact }) => compact ? 8 : 10,
		padding: ({ compact }) => compact ? "4px 6px" : "8px 10px",
		width: ({ fullWidth }) => fullWidth ? "100%" : 280,
		maxWidth: "100%",
		boxSizing: "border-box",
	},
	playBtn: {
		flexShrink: 0,
		width: ({ compact }) => compact ? 34 : 36,
		height: ({ compact }) => compact ? 34 : 36,
		borderRadius: "50%",
		border: "none",
		background: theme.palette.primary.main,
		color: "#fff",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "pointer",
		transition: "filter 0.15s ease",
		boxShadow: theme.mode === "light"
			? "0 1px 3px rgba(0,0,0,0.18)"
			: "0 1px 3px rgba(0,0,0,0.45)",
		"&:hover": { filter: "brightness(1.08)" },
	},
	body: {
		flex: 1,
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		gap: 2,
	},
	waveContainer: {
		width: "100%",
		height: ({ compact }) => compact ? 30 : 28,
		cursor: "pointer",
	},
	footer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		fontSize: 11,
		color: theme.mode === "light" ? "#6b7785" : "#9aa6b1",
		gap: 8,
	},
	time: {
		fontVariantNumeric: "tabular-nums",
	},
	rateBtn: {
		background: theme.mode === "light" ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)",
		color: "inherit",
		border: "none",
		borderRadius: 10,
		padding: "1px 8px",
		fontSize: 11,
		fontWeight: 600,
		cursor: "pointer",
		transition: "background 0.15s ease",
		minWidth: 32,
		"&:hover": {
			background: theme.mode === "light" ? "rgba(0,0,0,0.10)" : "rgba(255,255,255,0.14)",
		},
	},
}));

const formatTime = (s) => {
	if (!isFinite(s) || s < 0) s = 0;
	const m = Math.floor(s / 60);
	const sec = Math.floor(s % 60);
	return `${m}:${sec.toString().padStart(2, "0")}`;
};

const isAbortError = (err) => (
	err?.name === "AbortError" ||
	String(err?.message || err).toLowerCase().includes("abort")
);

const createAbortError = () => {
	try {
		return new DOMException("Audio player unmounted", "AbortError");
	} catch {
		const err = new Error("Audio player unmounted");
		err.name = "AbortError";
		return err;
	}
};

const AudioModal = ({ url, fullWidth = false, compact = false, variant = "default" }) => {
	const classes = useStyles({ fullWidth, compact, variant });
	const theme = useTheme();
	const containerRef = useRef(null);
	const wsRef = useRef(null);
	const [playing, setPlaying] = useState(false);
	const [time, setTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [rate, setRate] = useState(parseFloat(localStorage.getItem(LS_NAME) || "1"));

	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
	const sourceUrl = isIOS && typeof url === "string" ? url.replace(/\.ogg(\?|$)/, ".mp3$1") : url;

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		let ws;
		let cancelled = false;

		const init = () => {
			if (cancelled || !containerRef.current) return;
			if (containerRef.current.offsetWidth === 0) {
				requestAnimationFrame(init);
				return;
			}
			ws = WaveSurfer.create({
				container: containerRef.current,
				waveColor: theme.mode === "light" ? "rgba(11,125,218,0.45)" : "rgba(83,189,235,0.45)",
				progressColor: theme.mode === "light" ? "#0b7dda" : "#53bdeb",
				cursorColor: "transparent",
				barWidth: 2,
				barGap: 1,
				barRadius: 2,
				height: 28,
				normalize: true,
				interact: true,
				fillParent: true,
				minPxPerSec: 0,
				autoScroll: false,
				autoCenter: false,
			});
			wsRef.current = ws;
			ws.on("ready", () => setDuration(ws.getDuration() || 0));
			ws.on("audioprocess", (t) => setTime(t));
			ws.on("timeupdate", (t) => setTime(t));
			ws.on("seeking", (t) => setTime(t));
			ws.on("play", () => setPlaying(true));
			ws.on("pause", () => setPlaying(false));
			ws.on("error", (err) => {
				if (isAbortError(err)) return;
				console.error(err);
			});
			ws.on("finish", () => { setPlaying(false); ws.setTime(0); setTime(0); });
			ws.load(sourceUrl).catch((err) => {
				if (isAbortError(err)) return;
				console.error(err);
			});
		};

		init();
		return () => {
			cancelled = true;
			try {
				const controller = ws?.abortController;
				if (controller && !controller.signal?.aborted) {
					controller.abort(createAbortError());
				}
			} catch {}
			try { ws?.destroy(); } catch (err) {
				if (!isAbortError(err)) console.error(err);
			}
			wsRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sourceUrl]);

	useEffect(() => {
		const ws = wsRef.current;
		if (ws && ws.setPlaybackRate) {
			try { ws.setPlaybackRate(rate); } catch {}
		}
		localStorage.setItem(LS_NAME, String(rate));
	}, [rate]);

	const togglePlay = useCallback(() => {
		const ws = wsRef.current;
		if (!ws) return;
		if (ws.isPlaying()) ws.pause();
		else ws.play();
	}, []);

	const cycleRate = useCallback(() => {
		setRate((cur) => {
			const idx = RATES.indexOf(cur);
			return RATES[(idx + 1) % RATES.length] ?? 1;
		});
	}, []);

	return (
		<div className={classes.root} data-audio-player-variant={variant}>
			<button type="button" className={classes.playBtn} onClick={togglePlay}>
				{playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" style={{ marginLeft: 1 }} />}
			</button>
			<div className={classes.body}>
				<div className={classes.waveContainer} ref={containerRef} />
				<div className={classes.footer}>
					<span className={classes.time}>
						{formatTime(playing || time > 0 ? time : duration)}
					</span>
					<button type="button" className={classes.rateBtn} onClick={cycleRate}>
						{rate}x
					</button>
				</div>
			</div>
		</div>
	);
};

export default AudioModal;
